// Collapsible deep analytics section — Path A/B/AB breakdown, MTD/YTD, best/worst day

import React, { useState, useEffect } from 'react';
import { fmtRs, fmtPct, pnlColor } from './dashboardUtils';

function PathCard({ path, data }) {
  if (!data || data.total_trades === 0) return null;
  const pathLabel = {
    'A': 'Path A — Pullback Recovery',
    'B': 'Path B — Early Climb',
    'AB': 'Path AB — HIGH Conviction (both fired)',
    'UNKNOWN': 'Path Unknown'
  };
  return (
    <div style={{
      background: 'var(--bg1)',
      border: '1px solid var(--bgray)',
      borderRadius: 6,
      padding: 12,
      flex: 1,
      minWidth: 260,
    }}>
      <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--t1)', marginBottom: 8 }}>
        {pathLabel[path] || path}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6, fontSize: 11 }}>
        <div>Trades: <b>{data.total_trades}</b></div>
        <div>WR: <b style={{color: data.win_rate_pct >= 50 ? '#0d7f3f' : '#c92a2a'}}>{data.win_rate_pct.toFixed(1)}%</b></div>
        <div>Wins: <b style={{color: '#0d7f3f'}}>{data.wins}</b></div>
        <div>Losses: <b style={{color: '#c92a2a'}}>{data.losses}</b></div>
        <div>Total P&L:</div>
        <div style={{fontWeight: 700, color: pnlColor(data.total_pnl)}}>{fmtRs(data.total_pnl)}</div>
        <div>Avg trade:</div>
        <div style={{color: pnlColor(data.avg_pnl)}}>{fmtRs(data.avg_pnl)}</div>
        <div>Avg win:</div>
        <div style={{color: '#0d7f3f'}}>{fmtRs(data.avg_win)}</div>
        <div>Avg loss:</div>
        <div style={{color: '#c92a2a'}}>{fmtRs(data.avg_loss)}</div>
        <div>Best trade:</div>
        <div style={{color: '#0d7f3f'}}>{fmtRs(data.best_trade)}</div>
        <div>Worst trade:</div>
        <div style={{color: '#c92a2a'}}>{fmtRs(data.worst_trade)}</div>
      </div>
    </div>
  );
}

export default function AnalyticsBlock({ strategyKey }) {
  const [expanded, setExpanded] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchAnalytics = async () => {
    setLoading(true);
    try {
      const r = await fetch(`/${strategyKey}/analytics`);
      const j = await r.json();
      setData(j);
    } catch (e) {
      console.error('Analytics fetch failed:', e);
    }
    setLoading(false);
  };

  useEffect(() => {
    if (expanded && !data) fetchAnalytics();
  }, [expanded]);

  return (
    <div style={{
      background: '#fff',
      border: '1px solid #ddd',
      borderRadius: 8,
      padding: 12,
      marginBottom: 12,
    }}>
      <div 
        onClick={() => setExpanded(!expanded)}
        style={{
          cursor: 'pointer',
          fontWeight: 600,
          fontSize: 13,
          color: 'var(--t1)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <span>{expanded ? '▼' : '▶'} Analytics — Path breakdown, MTD/YTD, best/worst day</span>
        {expanded && (
          <span 
            style={{ fontSize: 10, color: '#0050b3', cursor: 'pointer' }}
            onClick={(e) => { e.stopPropagation(); fetchAnalytics(); }}
          >
            Refresh
          </span>
        )}
      </div>

      {expanded && (
        <div style={{ marginTop: 12 }}>
          {loading && <div style={{color: '#888'}}>Loading analytics...</div>}
          {data && !loading && (
            <>
              {/* Period P&L */}
              <div style={{ display: 'flex', gap: 12, marginBottom: 12, flexWrap: 'wrap' }}>
                <div style={{
                  background: 'var(--bg1)',
                  border: '1px solid var(--bgray)',
                  borderRadius: 6,
                  padding: 10,
                  flex: 1,
                  minWidth: 150,
                }}>
                  <div style={{fontSize: 10, color: 'var(--t3)', textTransform: 'uppercase'}}>Month to Date</div>
                  <div style={{fontSize: 16, fontWeight: 700, color: pnlColor(data.period_pnl.mtd_pnl)}}>
                    {fmtRs(data.period_pnl.mtd_pnl)}
                  </div>
                  <div style={{fontSize: 10, color: 'var(--t3)'}}>{data.period_pnl.mtd_trades} trades</div>
                </div>
                <div style={{
                  background: 'var(--bg1)',
                  border: '1px solid var(--bgray)',
                  borderRadius: 6,
                  padding: 10,
                  flex: 1,
                  minWidth: 150,
                }}>
                  <div style={{fontSize: 10, color: 'var(--t3)', textTransform: 'uppercase'}}>Year to Date</div>
                  <div style={{fontSize: 16, fontWeight: 700, color: pnlColor(data.period_pnl.ytd_pnl)}}>
                    {fmtRs(data.period_pnl.ytd_pnl)}
                  </div>
                  <div style={{fontSize: 10, color: 'var(--t3)'}}>{data.period_pnl.ytd_trades} trades</div>
                </div>
                <div style={{
                  background: 'var(--bg1)',
                  border: '1px solid var(--bgray)',
                  borderRadius: 6,
                  padding: 10,
                  flex: 1,
                  minWidth: 150,
                }}>
                  <div style={{fontSize: 10, color: 'var(--t3)', textTransform: 'uppercase'}}>Avg Daily P&L</div>
                  <div style={{fontSize: 16, fontWeight: 700, color: pnlColor(data.daily_aggregate.avg_daily_pnl)}}>
                    {fmtRs(data.daily_aggregate.avg_daily_pnl)}
                  </div>
                  <div style={{fontSize: 10, color: 'var(--t3)'}}>{data.daily_aggregate.trading_days} days traded</div>
                </div>
                {(() => {
                  const totalWins = Object.values(data.path_breakdown).reduce((s, p) => s + (p.wins || 0), 0);
                  const totalLosses = Object.values(data.path_breakdown).reduce((s, p) => s + (p.losses || 0), 0);
                  const totalTrades = totalWins + totalLosses;
                  const lifetimeWR = totalTrades > 0 ? (totalWins / totalTrades) * 100 : 0;
                  const wrColor = lifetimeWR >= 50 ? '#0d7f3f' : '#c92a2a';
                  return (
                    <div style={{
                      background: 'var(--bg1)',
                      border: '1px solid var(--bgray)',
                      borderRadius: 6,
                      padding: 10,
                      flex: 1,
                      minWidth: 150,
                    }}>
                      <div style={{fontSize: 10, color: 'var(--t3)', textTransform: 'uppercase'}}>Lifetime Win Rate</div>
                      <div style={{fontSize: 16, fontWeight: 700, color: wrColor}}>
                        {totalTrades > 0 ? lifetimeWR.toFixed(1) + '%' : '—'}
                      </div>
                      <div style={{fontSize: 10, color: 'var(--t3)'}}>{totalWins}W / {totalLosses}L</div>
                    </div>
                  );
                })()}
              </div>

              {/* Best / Worst Day */}
              {data.daily_aggregate.best_day && (
                <div style={{ display: 'flex', gap: 12, marginBottom: 12, flexWrap: 'wrap' }}>
                  <div style={{
                    background: '#e6f7e6',
                    border: '1px solid #0d7f3f',
                    borderRadius: 6,
                    padding: 10,
                    flex: 1,
                    minWidth: 220,
                  }}>
                    <div style={{fontSize: 10, color: '#0d7f3f', textTransform: 'uppercase'}}>🏆 Best Day</div>
                    <div style={{fontSize: 16, fontWeight: 700, color: '#0d7f3f'}}>
                      {fmtRs(data.daily_aggregate.best_day.pnl)}
                    </div>
                    <div style={{fontSize: 11, color: 'var(--t2)'}}>
                      {data.daily_aggregate.best_day.date} · {data.daily_aggregate.best_day.trades} trades
                    </div>
                  </div>
                  <div style={{
                    background: '#fdecec',
                    border: '1px solid #c92a2a',
                    borderRadius: 6,
                    padding: 10,
                    flex: 1,
                    minWidth: 220,
                  }}>
                    <div style={{fontSize: 10, color: '#c92a2a', textTransform: 'uppercase'}}>💥 Worst Day</div>
                    <div style={{fontSize: 16, fontWeight: 700, color: '#c92a2a'}}>
                      {fmtRs(data.daily_aggregate.worst_day.pnl)}
                    </div>
                    <div style={{fontSize: 11, color: 'var(--t2)'}}>
                      {data.daily_aggregate.worst_day.date} · {data.daily_aggregate.worst_day.trades} trades
                    </div>
                  </div>
                </div>
              )}

              {/* Path Breakdown */}
              <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--t1)', marginBottom: 8, marginTop: 12 }}>
                Path Breakdown
              </div>
              <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                {Object.entries(data.path_breakdown).map(([path, pathData]) => (
                  <PathCard key={path} path={path} data={pathData} />
                ))}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
